import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useDocumentSelectionContext } from '../contexts/DocumentSelectionContext';
import type { GroupedDocuments, SelectionSource } from '../types/documentSelection';
import type { MentionFilters } from '../components/chat/MentionTypes';

export const useDocumentSelection = () => {
  const {
    state,
    selectDocuments: contextSelect,
    deselectDocuments: contextDeselect,
    setFilters: contextSetFilters,
    clearSelection,
    setDocument,
    loadDocument,
    setAvailableDocumentIds,
  } = useDocumentSelectionContext();

  const selectedDocuments = useMemo(() => {
    return state.selectedIds
      .map((id) => state.documents.get(id))
      .filter((doc): doc is NonNullable<typeof doc> => !!doc);
  }, [state.selectedIds, state.documents]);

  const groupedDocuments: GroupedDocuments = useMemo(() => {
    const groups: GroupedDocuments = {
      manual: [],
      'mention-filters': [],
      'ai-system': [],
    };

    selectedDocuments.forEach((doc) => {
      const source = state.sources.get(doc.id) || 'ai-system';
      groups[source].push(doc);
    });

    return groups;
  }, [selectedDocuments, state.sources]);

  const hasActiveFilters = useMemo(() => Object.keys(state.filters).length > 0, [state.filters]);

  const aiSuggestedIds = useMemo(() => {
    const ids = new Set<string>();
    state.sources.forEach((source, id) => {
      if (source === 'ai-system') ids.add(id);
    });
    return ids;
  }, [state.sources]);

  // REMOVED: documentsRef no longer needed since we don't load documents on selection

  const selectDocuments = useCallback(
    (documentIds: string[], source: SelectionSource = 'manual') => {
      contextSelect(documentIds, source);
      // Load document metadata into context so SelectedDocsTab can display them
      documentIds.forEach((id) => {
        loadDocument(id).catch(() => {});
      });
    },
    [contextSelect, loadDocument]
  );

  const deselectDocuments = useCallback(
    (documentIds: string[]) => {
      contextDeselect(documentIds);
    },
    [contextDeselect]
  );

  const setFilters = useCallback(
    (filters: MentionFilters) => {
      contextSetFilters(filters);
    },
    [contextSetFilters]
  );

  return {
    selectedIds: state.selectedIds,
    selectedDocuments,
    groupedDocuments,
    hasActiveFilters,
    filters: state.filters,
    sources: state.sources,
    selectDocuments,
    deselectDocuments,
    setFilters,
    clearSelection,
    setDocument,
    loadDocument,
    setAvailableDocumentIds,
    aiSuggestedIds,
  };
};
